import { Quote, ArrowLeft, ArrowRight, Star } from "lucide-react";
import { useLang } from "./LanguageContext";
import { Reveal } from "./Reveal";
import { useAutoCarousel } from "./useAutoCarousel";

export const Testimonials = () => {
  const { t } = useLang();
  const items = t.testimonials.items;
  const { emblaRef, selected, scrollTo, scrollNext, scrollPrev } = useAutoCarousel(5500, { align: "start" });

  return (
    <section className="relative py-24 md:py-32 border-t border-white/10 bg-[#0a0a0a]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div className="max-w-2xl">
              <span className="text-xs font-bold tracking-[0.25em] uppercase text-[#00f0ff]">
                {t.testimonials.overline}
              </span>
              <h2 className="font-display font-bold text-3xl md:text-5xl tracking-tight text-white mt-4 leading-tight">
                {t.testimonials.title}
              </h2>
            </div>
            <div className="flex gap-3">
              <button
                data-testid="testimonial-prev"
                onClick={() => scrollPrev()}
                aria-label="Previous"
                className="flex h-12 w-12 items-center justify-center border border-white/15 text-white hover:border-[#ccff00] hover:text-[#ccff00] transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <button
                data-testid="testimonial-next"
                onClick={() => scrollNext()}
                aria-label="Next"
                className="flex h-12 w-12 items-center justify-center border border-white/15 text-white hover:border-[#ccff00] hover:text-[#ccff00] transition-colors"
              >
                <ArrowRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </Reveal>

        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex -ml-6">
            {items.map((item, i) => (
              <div
                key={i}
                data-testid={`testimonial-${i}`}
                className="min-w-0 shrink-0 grow-0 basis-full md:basis-1/2 lg:basis-1/3 pl-6"
              >
                <div className="group h-full border border-white/10 bg-white/[0.02] p-8 hover:border-white/25 transition-colors flex flex-col">
                  <div className="flex items-center justify-between">
                    <Quote className="h-8 w-8 text-[#ccff00]" />
                    <div className="flex gap-0.5">
                      {[...Array(5)].map((_, s) => (
                        <Star key={s} className="h-4 w-4 fill-[#ccff00] text-[#ccff00]" />
                      ))}
                    </div>
                  </div>
                  <p className="mt-6 text-zinc-200 font-light leading-relaxed flex-1">"{item.quote}"</p>
                  <div className="mt-8 pt-6 border-t border-white/10">
                    <div className="flex items-center gap-3">
                      <span className="flex h-11 w-11 items-center justify-center bg-[#ccff00] text-black font-display font-black">
                        {item.name.charAt(0)}
                      </span>
                      <div className="leading-tight">
                        <span className="block font-semibold text-white">{item.name}</span>
                        <span className="block text-xs text-zinc-500">{item.role}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 flex items-center gap-2">
          {items.map((_, i) => (
            <button
              key={i}
              data-testid={`testimonial-dot-${i}`}
              onClick={() => scrollTo(i)}
              aria-label={`Testimonial ${i + 1}`}
              className={`h-1.5 transition-all duration-300 ${
                selected === i ? "w-10 bg-[#ccff00]" : "w-4 bg-white/20 hover:bg-white/40"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
